import { Controller } from '@hotwired/stimulus'
import intlTelInput from 'intl-tel-input/intlTelInputWithUtils'

let importCSS

export default class extends Controller {
  static targets = ['input', 'submit']

  // intl-tel-input v28 returns string error codes from getValidationError() instead
  // of integers. Map each to a user-facing message; null returns mean "valid",
  // and any unmapped code falls through to the generic "Invalid number" below.
  get errorMap() {
    return {
      INVALID_COUNTRY_CODE: 'Invalid country code',
      TOO_SHORT: 'Too short',
      TOO_LONG: 'Too long',
      INVALID_LENGTH: 'Invalid number',
      IS_POSSIBLE_LOCAL_ONLY: 'Invalid number',
    }
  }

  initialize() {
    importCSS ??= import('intl-tel-input/styles')
  }

  // initializations are done here, after connecting
  async connect() {
    await importCSS
    // initialize intlTelInput for each input target
    this.itis = this.inputTargets.map((input) => {
      const iti = intlTelInput(input, {
        strictMode: true,
        allowedNumberTypes: ['MOBILE', 'FIXED_LINE', 'FIXED_LINE_OR_MOBILE'],
        initialCountry: 'us',
        countryOrder: [
          'us',
          'cn',
          'in',
          'ca',
          'pr',
          'kr',
          'mx',
          'ng',
          'gh',
          'br',
          'gb',
          // Remaining countries will be listed in alphabetical order
        ],
        formatOnDisplay: true,
        formatAsYouType: true,
        nationalMode: false,
        // v28 flipped separateDialCode default to true, which would render a
        // static "+1" segment outside the input and shift the form layout.
        // Pin to false to preserve the current single-input visual.
        separateDialCode: false,
        hiddenInput: (telInputName) => {
          return { phone: telInputName, country: 'country_code' }
        },
      })

      // set the iti.id so we can use it to retrieve it from this.itis
      input.dataset.iti = iti.id

      input.addEventListener('input', this.validate.bind(this))
      input.addEventListener('change', this.validate.bind(this))
      input.addEventListener('paste', this.validate.bind(this))
      input.addEventListener('countrychange', this.validate.bind(this))

      // Initialize error message
      this.setError(input, iti)

      // add them to this.itis so we can retrieve it later
      return iti
    })
  }

  // validate number
  validate(event) {
    // Prevent infinite loop from intl-tel-input custom events, except countrychange
    if (!event.isTrusted && event.type !== 'countrychange') {
      return
    }

    // get current iti from event target
    const iti = this.itis[event.target.dataset.iti]
    const target = event.target

    this.setError(target, iti)
  }

  setError(target, iti) {
    const previousDescribedBy = target.getAttribute('aria-describedby') || ''
    const inputRequired =
      target.getAttribute('required') ||
      target.getAttribute('aria-required') ||
      ''

    if (iti.getNumber().length === 0 && inputRequired != 'true') {
      // If empty and not required

      // Remove any error or valid messages
      document.querySelector(`#${target.dataset.err}`).innerHTML = ''
      document.querySelector(`#${target.dataset.err}`).classList.add('hide')
      document.querySelector(`#${target.dataset.valid}`).classList.add('hide')
      target.classList.remove('error')
      const cleanedDescribedBy = previousDescribedBy.replaceAll(
        new RegExp(`(${target.dataset.err}|${target.dataset.valid})`, 'g'),
        '',
      )
      target.setAttribute('aria-invalid', 'false')

      // If aria-describedby will now be empty, remove it
      if (cleanedDescribedBy.trim() === '') {
        target.removeAttribute('aria-describedby')
      } else {
        target.setAttribute('aria-describedby', cleanedDescribedBy)
      }
    } else if (iti.isValidNumber()) {
      // If not empty and valid

      // Remove any error messages
      document.querySelector(`#${target.dataset.err}`).innerHTML = ''
      document.querySelector(`#${target.dataset.err}`).classList.add('hide')
      target.classList.remove('error')
      const cleanedDescribedBy =
        previousDescribedBy.replaceAll(target.dataset.err, '') || ''

      // Set valid message
      document
        .querySelector(`#${target.dataset.valid}`)
        .classList.remove('hide')
      target.setAttribute('aria-invalid', 'false')
      if (!cleanedDescribedBy.includes(target.dataset.valid)) {
        target.setAttribute(
          'aria-describedby',
          [cleanedDescribedBy, target.dataset.valid].join(' ').trim(),
        )
      }

      // Hack to force the input to be formatted
      iti.setNumber(iti.getNumber())
    } else {
      // If not empty and not valid

      // Remove valid message
      document.querySelector(`#${target.dataset.valid}`).classList.add('hide')
      const cleanedDescribedBy =
        previousDescribedBy.replaceAll(target.dataset.valid, '') || ''

      // Set error message
      target.classList.add('error')
      document.querySelector(`#${target.dataset.err}`).innerHTML =
        this.errorMap[iti.getValidationError()] || 'Invalid number'
      document.querySelector(`#${target.dataset.err}`).classList.remove('hide')
      target.setAttribute('aria-invalid', 'true')

      if (!cleanedDescribedBy.includes(target.dataset.err)) {
        target.setAttribute(
          'aria-describedby',
          [cleanedDescribedBy, target.dataset.err].join(' ').trim(),
        )
      }
    }
    if (this.hasSubmitTarget) {
      this.updateSubmitButton()
    }
  }

  // Check if there are any mobile number inputs that are not valid
  // if there are, disable submit button. If there aren't, remove disabled
  // attribute
  updateSubmitButton() {
    const result = this.inputTargets.some((input) => {
      return input.classList.contains('error')
    })

    this.submitTarget.disabled = result
  }
}
